// The small, always-loaded part of the world-game kit: piece sizes and names,
// the "which piece is being placed" arm, the look of each theme, and the
// leaderboard ids for fastest times. The runtime (hunters, HUD, audio) and
// the builder visuals live in chunks that load only when a world has a
// world-game piece or the builder places / selects one.

import type { PrimitiveItem } from '../../../stores/worldStore';
import { WORLD_GAME_DEFAULTS, type WorldGameConfig, type WorldGameRole, type WorldGameTheme } from './worldGameTypes';
import { formatTime, pointsToTime } from './worldGameLogic';

export { formatTime, pointsToTime };

/** Kit art on R2 (from The Scarecrow's Harvest package). */
export const KIT_ASSETS = {
  ghosts: 'https://assets.gathrxr.com/environments/harvest/ghost-apparitions-v1.webp',
  catchFace: 'https://assets.gathrxr.com/environments/harvest/front-scarecrow-v1.webp',
};
const H = 'https://assets.gathrxr.com/environments/harvest/';
/** Cutout figures that come with the kit (full-resolution single figures with alpha). */
export const KIT_FIGURES: { id: string; label: string; url: string; height: number }[] = [
  { id: 'child-1', label: 'Corn girl', url: `${H}cornfield-child-1-v1.webp`, height: 1.42 },
  { id: 'child-2', label: 'Straw-hat boy', url: `${H}cornfield-child-2-v1.webp`, height: 1.5 },
  { id: 'child-3', label: 'Pale girl', url: `${H}cornfield-child-3-v1.webp`, height: 1.36 },
  { id: 'witch', label: 'Witch', url: `${H}haunted-witch-v1.webp`, height: 1.85 },
  { id: 'rootling', label: 'Root creature', url: `${H}haunted-rootling-v1.webp`, height: 1.75 },
  { id: 'scarecrow', label: 'Scarecrow', url: `${H}haunted-scarecrow-v1.webp`, height: 2.0 },
];

/** How to collect, per device (welcome card, launch card, rules sign). */
export const COLLECT_HOW = {
  desktop: 'Walk into it, or click it',
  touch: 'Walk into it, or tap it',
  vr: 'Touch it with your hand or controller',
} as const;
export const MOVE_HOW = {
  desktop: 'WASD / arrow keys to move · Shift to run · drag to look',
  touch: 'Left thumb joystick to move · drag to look',
  vr: 'Thumbstick to move or teleport · or walk around',
} as const;
export const DEFAULT_RULES = 'Find every glowing item: look for the light beams\nAvoid the hunters: a catch costs you an item\nReach the finish with everything. Fastest time wins';
export const rulesOf = (cfg: WorldGameConfig | null | undefined) =>
  (cfg?.rules || DEFAULT_RULES).split(/\n+/).map((s) => s.trim()).filter(Boolean).slice(0, 3);

export const ROLE_LABEL: Record<WorldGameRole, string> = {
  start: 'Game start',
  collectible: 'Collectible',
  patrol: 'Hunter',
  goal: 'Finish zone',
  scare: 'Scare zone',
  figure: 'Cutout figure',
  powerup: 'Power-up',
};

export const ROLE_HINT: Record<WorldGameRole, string> = {
  start: 'Where a run begins: walk up to see the rules, best times and Start',
  collectible: 'An item players pick up by walking into it (or touching it in VR)',
  patrol: 'A hunter that walks a path: getting caught costs an item',
  goal: 'Reach this zone holding every item to finish the run',
  scare: 'Walk through it and an apparition jumps out (once in a while)',
  figure: 'A cutout figure standing in the world: it watches you, or vanishes when you get close',
  powerup: 'A pick-up during a run: a crow feather freezes the hunters, a lantern lights the way to the next item',
};

/** Pick box (metres, unscaled) per piece; the primitive's origin is its centre. Goal and scare zones are unit boxes sized by the primitive's scale. */
export const ROLE_BOX: Record<WorldGameRole, [number, number, number]> = {
  start: [1.1, 1.7, 0.9],
  collectible: [0.8, 0.8, 0.8],
  patrol: [1.1, 2.5, 1.1],
  goal: [1, 1, 1],
  scare: [1, 1, 1],
  figure: [0.7, 1.5, 0.3],
  powerup: [0.8, 0.8, 0.8],
};
/** Default scale when placed (zones start as a doorway-sized box). */
export const ROLE_SCALE: Record<WorldGameRole, [number, number, number]> = {
  start: [1, 1, 1], collectible: [1, 1, 1], patrol: [1, 1, 1], goal: [4, 3, 2], scare: [4, 3, 4], figure: [1, 1, 1], powerup: [1, 1, 1],
};
/** Primitive y that rests it on a floor at y = 0 (collectibles float at chest height). */
export const roleRestY = (role: WorldGameRole): number =>
  role === 'collectible' || role === 'powerup' ? 1.0 : role === 'goal' || role === 'scare' ? ROLE_SCALE[role][1] / 2 : ROLE_BOX[role][1] / 2;

export const THEMES: Record<WorldGameTheme, { label: string; c1: string; c2: string; ink: string }> = {
  harvest: { label: 'Harvest', c1: '#ff8a2a', c2: '#ffd166', ink: '#1b0d04' },
  neon: { label: 'Neon', c1: '#ff4fa3', c2: '#22d3c5', ink: '#12041a' },
  frost: { label: 'Frost', c1: '#7dd3fc', c2: '#e0f2fe', ink: '#04121b' },
};
export const themeOf = (cfg: Pick<WorldGameConfig, 'theme'> | null | undefined) => THEMES[cfg?.theme ?? 'harvest'] ?? THEMES.harvest;

/** A fresh config for a newly placed piece of `game`. */
export function newWorldGameConfig(role: WorldGameRole, game: string): WorldGameConfig {
  const base: WorldGameConfig = { role, game };
  if (role === 'start') {
    const d = WORLD_GAME_DEFAULTS;
    return { ...base, title: d.title, blurb: d.blurb, howTo: d.howTo, goal: d.blurb, rules: DEFAULT_RULES, showGuide: true, itemName: d.itemName, required: 0, timeLimit: 0, lives: 0, penalty: 'item', penaltySeconds: d.penaltySeconds, board: d.board, theme: 'harvest', race: true };
  }
  if (role === 'collectible') return { ...base, look: 'pumpkin' };
  if (role === 'patrol') return { ...base, look: 'scarecrow', path: [], loop: false, speed: WORLD_GAME_DEFAULTS.speed, pause: WORLD_GAME_DEFAULTS.pause, phase: 0, catchRadius: WORLD_GAME_DEFAULTS.catchRadius };
  if (role === 'scare') return { ...base, frame: 0, cooldown: WORLD_GAME_DEFAULTS.cooldown, kind: 'ghost' };
  if (role === 'powerup') return { ...base, power: 'freeze', seconds: 5 };
  if (role === 'figure') return { ...base, imageUrl: KIT_FIGURES[0].url, cols: 1, height: KIT_FIGURES[0].height, behavior: 'watch' };
  return base;
}

export const worldGamePieceName = (cfg: WorldGameConfig | null | undefined) => (cfg ? ROLE_LABEL[cfg.role] : 'World game');

// The builder arms "place a world-game piece"; the place click reads it back.
let armed: { role: WorldGameRole; game: string } = { role: 'collectible', game: 'game' };
export function armWorldGamePlacement(role: WorldGameRole, game: string) { armed = { role, game }; }
export function armedWorldGame() { return armed; }

/** The game key the builder offers for a new piece: the world's existing game, else 'game'. */
export function defaultGameKey(prims: Record<string, PrimitiveItem>): string {
  for (const p of Object.values(prims)) if (p.shape === 'worldgame' && p.worldGame?.role === 'start') return p.worldGame.game;
  for (const p of Object.values(prims)) if (p.shape === 'worldgame' && p.worldGame) return p.worldGame.game;
  return 'game';
}

// ---- leaderboards ------------------------------------------------------------------
// Fastest times live on the arcade boards (arcade_games rows, see
// supabase/migrations/20261102_world_game_scores.sql) as points =
// 3,600,000 − ms, so a board sorted by points shows the fastest first.

export interface WorldGameBoardDef { id: string; title: string; colors: { primary: string; secondary: string }; /** 'time' (default): points are 3,600,000 − ms. 'count': points are a plain score (e.g. calls survived). */ units?: 'time' | 'count'; unitLabel?: string }
export const WORLD_GAME_BOARDS: WorldGameBoardDef[] = [
  { id: 'harvest', title: 'Harvest Hunt', colors: { primary: THEMES.harvest.c1, secondary: THEMES.harvest.c2 } },
  { id: 'worldgame', title: 'World Hunt', colors: { primary: THEMES.neon.c1, secondary: THEMES.neon.c2 } },
  { id: 'blackthorn', title: 'Blackthorn House', colors: { primary: '#d9b27c', secondary: '#4f7a6a' } },
  { id: 'lastbite', title: 'Last Bite', colors: { primary: '#f0c35c', secondary: '#c8432a' } },
  { id: 'carnival', title: "Mister Giggles' Carnival", colors: { primary: '#f2c14e', secondary: '#e14b3b' }, units: 'count', unitLabel: 'calls' },
];
export const worldGameBoard = (id: string | null | undefined) => WORLD_GAME_BOARDS.find((b) => b.id === id) ?? null;
/** A board row's points as a time (2:31.4). */
export const formatBoardTime = (points: number) => formatTime(pointsToTime(points));
